import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import Accordion from "../../components/widgets/Accordion";
import { useStore } from "../../js/state/store";

export const TIPS = [
  {
    id: "choosing",
    title: "Tips for choosing a home",
    content: (
      <div>
        <p>
          Rank your top five must-haves before the first tour. If a home misses two of them, it
          is usually not “the one” — it is a distraction.
        </p>
        <ul>
          <li>Separate deal-breakers from nice-to-haves so showings stay focused.</li>
          <li>Walk the block at the time of day you would actually live there.</li>
          <li>Look past staging: light, storage, layout, and noise travel with you; furniture does not.</li>
          <li>Keep a simple like/dislike note after every tour so later homes are compared fairly.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "communicate",
    title: "Communicating clearly with your agent",
    content: (
      <div>
        <p>
          Your agent cannot read a vibe. They can search commute minutes, lot type, bedroom
          count, and “main-floor primary.” This checklist is that translation.
        </p>
        <ul>
          <li>Say what you want the home to <em>do</em> (work from home, host family, age in place).</li>
          <li>Reply promptly on showings — the best homes often move in days, not weeks.</li>
          <li>Share financing facts early (pre-approval amount, cash vs. loan, HOA comfort).</li>
          <li>If something felt off on a tour, name it. Vague “not quite” leaves your agent guessing.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "fair-housing",
    title: "Fair Housing–compliant language",
    content: (
      <div>
        <p>
          The Fair Housing Act prohibits housing discrimination based on race, color, national
          origin, religion, sex (including gender identity and sexual orientation), familial
          status, and disability. Your search should describe the <strong>property and location
          features</strong>, not the people who live nearby.
        </p>
        <ul>
          <li>
            Avoid asking for a “good neighborhood,” “the right kind of people,” or school quality
            as a stand-in for who lives there.
          </li>
          <li>
            Do ask for commute time, lot size, HOA rules, flood risk, walkability to a grocery,
            or a home that fits your household’s access needs.
          </li>
          <li>
            School questions can focus on a specific program or campus you already researched —
            not coded language about “the best schools.”
          </li>
          <li>
            Your agent cannot steer you toward or away from areas based on protected
            characteristics. This checklist is designed to stay on housing features.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "mistakes",
    title: "Common buyer mistakes to avoid",
    content: (
      <div>
        <ul>
          <li>Skipping pre-approval and then losing a home you could have written on.</li>
          <li>Falling for the first showing because you are tired of looking — compare against your list.</li>
          <li>Ignoring HOA docs, flood maps, and insurance until after you are in love with the house.</li>
          <li>Waiving inspection in a hurry without understanding the tradeoff.</li>
          <li>Stretching the budget to the last dollar with no reserve for repairs or rate changes.</li>
          <li>Talking about your top price in front of the listing agent or in a crowded open house.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "escrow",
    title: "Preparing for escrow, inspections, and appraisal",
    content: (
      <div>
        <p>
          Once an offer is accepted, the clock is contractual. Know who does what before you are
          in it.
        </p>
        <ul>
          <li>
            <strong>Escrow / closing:</strong> a neutral party holds funds and documents. Respond
            quickly to ID, insurance, and wire instructions — and confirm wiring by a known phone
            number, never from an email alone.
          </li>
          <li>
            <strong>Inspections:</strong> general home plus specialists as needed (roof, sewer,
            chimney, pest). Attend if you can. Use findings to negotiate repairs or credits, not
            to relitigate whether you like the house.
          </li>
          <li>
            <strong>Appraisal:</strong> the lender’s value check. If it comes in low, you will
            talk through price, gap coverage, or walking — with your agent and lender, not in a
            panic text.
          </li>
          <li>Keep the transaction checklist in this app updated so nothing silent expires.</li>
        </ul>
      </div>
    ),
  },
];

export default function BuyerTips() {
  const saved = useStore().state.buyer.current.preferences.tipsAcknowledged;
  const [acknowledged, setAcknowledged] = useState(Boolean(saved));

  return (
    <ChecklistStep
      title="Buyer tips"
      lead="A short briefing before you tour and write offers. Expand each section, then confirm you have read them."
      preferenceKey="tipsAcknowledged"
      value={acknowledged}
    >
      <Accordion items={TIPS} />
      <label className="checkbox-item" style={{ marginTop: "1rem" }}>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />
        <span>I have read these tips and am ready to continue.</span>
      </label>
    </ChecklistStep>
  );
}
